import React, { useEffect, useRef, useState } from 'react';
import type { CallStatus } from '../../contexts/CallContext';
import type { RecordingStatus } from '../../api/callRecording';
import {
  RecBadge, RecordingNotice, recordingUi, showsRecordingNotice, StartRecordingButton, useStartRecording,
} from './RecordingIndicators';
import './call.css';

interface Props {
  status: CallStatus;
  /** Outgoing: the callee's device reported it is showing the call. */
  peerRinging?: boolean;
  peerName: string | null;
  peerAvatarUrl: string | null;
  muted: boolean;
  startedAt: number | null;
  endReason: string | null;
  minimized: boolean;
  /** True while the connected peer is talking (drives the avatar wave). */
  remoteSpeaking?: boolean;
  /** Server-side recording status of this call, if it has one. */
  recordingStatus?: RecordingStatus | null;
  /** `manual` ("During call"): records only once a participant starts it. */
  recordingStartMode?: 'auto' | 'manual' | null;
  /** Viewer-relative (server): may press Start recording now. */
  canStartRecording?: boolean;
  onStartRecording?: () => void | Promise<unknown>;
  onAccept: () => void;
  onReject: () => void;
  onToggleMute: () => void;
  onHangup: () => void;
  onToggleMinimize: () => void;
  onMinimize: () => void;
  onAddParticipant?: () => void;
}

function fmt(totalSec: number): string {
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Live MM:SS timer; only ticks while connected. */
function useCallTimer(status: CallStatus, startedAt: number | null): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (status !== 'connected') return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [status]);
  if (status !== 'connected' || !startedAt) return '0:00';
  return fmt(Math.max(0, Math.floor((now - startedAt) / 1000)));
}

// ── Inline icons (stroke = currentColor) ──────────────────────────────────
const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z" />
  </svg>
);
const PhoneOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
    <line x1="23" y1="1" x2="1" y2="23" />
  </svg>
);
const MicIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3Z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);
const MicOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="1" y1="1" x2="23" y2="23" />
    <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
    <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);
const MinimizeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="4 14 10 14 10 20" />
    <polyline points="20 10 14 10 14 4" />
    <line x1="14" y1="10" x2="21" y2="3" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);
const UserPlusIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>);
function initial(name: string | null): string {
  return (name || '?').charAt(0).toUpperCase();
}

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

/**
 * Makes the minimized pill freely draggable anywhere on screen.
 * Until the first drag it stays at its default CSS position (centered bottom);
 * once dragged we pin it via inline left/top. A small move threshold keeps
 * taps-to-expand and button clicks working — `consumedDrag()` swallows the
 * click that ends an actual drag so it doesn't double as a tap.
 */
function useDraggablePill() {
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const movedRef = useRef(false);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX;
    const py = e.clientY;
    const w = rect.width;
    const h = rect.height;
    const baseLeft = rect.left;
    const baseTop = rect.top;
    movedRef.current = false;

    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - px;
      const dy = ev.clientY - py;
      if (!movedRef.current && Math.hypot(dx, dy) < 4) return;
      movedRef.current = true;
      const margin = 8;
      setPos({
        left: clamp(baseLeft + dx, margin, window.innerWidth - w - margin),
        top: clamp(baseTop + dy, margin, window.innerHeight - h - margin),
      });
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  };

  /** True (once) if the click ending this interaction was actually a drag. */
  const consumedDrag = () => {
    if (!movedRef.current) return false;
    movedRef.current = false;
    return true;
  };

  const style: React.CSSProperties | undefined = pos
    ? { left: pos.left, top: pos.top, right: 'auto', bottom: 'auto', transform: 'none' }
    : undefined;

  return { onPointerDown, consumedDrag, style };
}

/** The single full-screen + minimized-pill call UI for every call state. */
export default function CallScreen(props: Props) {
  const {
    status, peerRinging = false, peerName, peerAvatarUrl, muted, startedAt, endReason,
    minimized, remoteSpeaking, onAccept, onReject, onToggleMute, onHangup, onToggleMinimize, onMinimize,
    onAddParticipant, recordingStatus = null, recordingStartMode = null, canStartRecording = false, onStartRecording,
  } = props;

  const timer = useCallTimer(status, startedAt);
  const drag = useDraggablePill();
  const isIncoming = status === 'incoming';
  const isRinging = status === 'incoming' || status === 'outgoing' || status === 'connecting';
  const recordingLive = recordingStatus === 'recording' && status !== 'ended';
  // Consent notice until the recording is live on a connected call (where
  // the REC badge takes over); a ringing side sees it even mid-recording.
  // A pending "During call" recording: "may be recorded" before connecting,
  // then the Start recording button (for whoever may press it) instead.
  const recUi = recordingUi(recordingStatus, recordingStartMode, status === 'connected', canStartRecording && !!onStartRecording);
  const recordingNotice = status !== 'ended' && showsRecordingNotice(recordingStatus)
    && !(status === 'connected' && recordingStatus === 'recording') && !recUi.hideNotice;
  const startRecording = useStartRecording(onStartRecording);

  // Minimized pill — keeps every call state out of the way while the user
  // continues using the app. Incoming calls retain accept/reject actions.
  if (minimized) {
    const ended = status === 'ended';
    const pillStatus =
      status === 'incoming' ? 'Incoming call'
      : status === 'outgoing' ? (peerRinging ? 'Ringing…' : 'Calling…')
      : status === 'connecting' ? 'Connecting…'
      : ended ? (endReason || 'Call ended')
      : timer;
    return (
      <div
        className="cs-pill"
        role="dialog"
        aria-label="Voice call"
        style={drag.style}
        onPointerDown={drag.onPointerDown}
        title="Drag to move"
      >
        <button
          type="button"
          className="cs-pill__id"
          onClick={() => { if (!drag.consumedDrag()) onToggleMinimize(); }}
          aria-label="Expand call"
          disabled={ended}
        >
          {peerAvatarUrl
            ? <img src={peerAvatarUrl} alt="" className="cs-pill__avatar" />
            : <span className="cs-pill__avatar--fallback">{initial(peerName)}</span>}
          <span className="cs-pill__text">
            <span className="cs-pill__name">{peerName || 'Call'}</span>
            <span className="cs-pill__status">
              {pillStatus}
              {recordingLive && <RecBadge compact />}
            </span>
          </span>
        </button>
        {isIncoming ? (
          <>
            <button
              type="button"
              className="cs-pill__btn cs-pill__btn--end"
              onClick={() => { if (!drag.consumedDrag()) onReject(); }}
              aria-label="Reject call"
            >
              <PhoneOffIcon />
            </button>
            <button
              type="button"
              className="cs-pill__btn cs-pill__btn--accept"
              onClick={() => { if (!drag.consumedDrag()) onAccept(); }}
              aria-label="Accept call"
            >
              <PhoneIcon />
            </button>
          </>
        ) : !ended && (
          <>
            <button
              type="button"
              className={`cs-pill__btn${muted ? ' cs-pill__btn--muted' : ''}`}
              onClick={() => { if (!drag.consumedDrag()) onToggleMute(); }}
              aria-label={muted ? 'Unmute' : 'Mute'}
            >
              {muted ? <MicOffIcon /> : <MicIcon />}
            </button>
            <button
              type="button"
              className="cs-pill__btn cs-pill__btn--end"
              onClick={() => { if (!drag.consumedDrag()) onHangup(); }}
              aria-label="Hang up"
            >
              <PhoneOffIcon />
            </button>
          </>
        )}
      </div>
    );
  }

  // Subtitle under the name (WhatsApp-style "… audio call").
  const statusLine =
    status === 'incoming' ? <>TeamLio audio call</>
    // Ringing only once the other device reported it is showing the call.
    : status === 'outgoing' ? <>{peerRinging ? 'Ringing' : 'Calling'}<span className="cs-dots" /></>
    : status === 'connecting' ? <>Connecting<span className="cs-dots" /></>
    : status === 'connected' ? <span className="cs-timer">{timer}</span>
    : <>{endReason || 'Call ended'}</>;

  const ringClass =
    status === 'connected' ? (remoteSpeaking ? 'is-speaking' : 'is-connected')
    : isRinging ? 'is-ringing'
    : '';

  return (
    <div
      className="cs-overlay"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onMinimize();
      }}
      role="dialog"
      aria-label={isIncoming ? 'Incoming call' : 'Voice call'}
    >
      <div className="cs-window">
        <div className="cs-card">
          <div className="cs-name">{peerName || 'Unknown'}</div>
          <div className="cs-status">
            {statusLine}
            {recordingLive && <RecBadge />}
          </div>
          {recordingNotice && <RecordingNotice mode={recUi.mode} />}
          {recUi.showStart && <StartRecordingButton onStart={startRecording.start} busy={startRecording.busy} />}
          <div className={`cs-avatar-wrap ${ringClass}`}>
            <span className="cs-ring" />
            <span className="cs-ring" />
            <span className="cs-ring" />
            {peerAvatarUrl
              ? <img src={peerAvatarUrl} alt="" className="cs-avatar" />
              : <span className="cs-avatar--fallback">{initial(peerName)}</span>}
          </div>
        </div>

        <div className="cs-controls">
          {isIncoming ? (
          <>
            <div className="cs-control">
              <button className="cs-btn cs-btn--decline" onClick={onReject} aria-label="Reject call">
                <PhoneOffIcon />
              </button>
              <span className="cs-control-label">Decline</span>
            </div>
            <div className="cs-control">
              <button className="cs-btn cs-btn--accept" onClick={onAccept} aria-label="Accept call">
                <PhoneIcon />
              </button>
              <span className="cs-control-label">Accept</span>
            </div>
          </>
        ) : status !== 'ended' ? (
          <>
            <div className="cs-control">
              <button
                className={`cs-btn${muted ? ' cs-btn--muted' : ''}`}
                onClick={onToggleMute}
                aria-label={muted ? 'Unmute' : 'Mute'}
              >
                {muted ? <MicOffIcon /> : <MicIcon />}
              </button>
              <span className="cs-control-label">{muted ? 'Unmute' : 'Mute'}</span>
            </div>
            {onAddParticipant && status === 'connected' && (
              <div className="cs-control">
                <button className="cs-btn" onClick={onAddParticipant} aria-label="Add participant"><UserPlusIcon /></button>
                <span className="cs-control-label">Add</span>
              </div>
            )}
            <div className="cs-control">
              <button className="cs-btn cs-btn--end" onClick={onHangup} aria-label="Hang up">
                <PhoneOffIcon />
              </button>
              <span className="cs-control-label">End</span>
            </div>
            <div className="cs-control">
              <button className="cs-btn" onClick={onToggleMinimize} aria-label="Minimize call">
                <MinimizeIcon />
              </button>
              <span className="cs-control-label">Minimize</span>
            </div>
          </>
        ) : null}
        </div>
      </div>
    </div>
  );
}
